/**
 * Central registry of TanStack Query keys. Every key is built here so
 * invalidation never depends on remembering an array literal's exact shape.
 *
 * `feedRoot` is the prefix every feed variant shares, which is what lets a new
 * post invalidate all of them in one call.
 */
export const queryKeys = {
  health: ['health'] as const,
  session: ['session'] as const,
  emailVerification: (token: string) => ['email-verification', token] as const,
  countries: ['countries'] as const,

  feedRoot: ['feed'] as const,
  feed: (query: Record<string, string>) => ['feed', query] as const,

  post: (id: string) => ['post', id] as const,
  postIdeals: (id: string) => ['post', id, 'ideals'] as const,

  comments: (postId: string) => ['comments', postId] as const,
  replies: (commentId: string) => ['replies', commentId] as const,

  bookmarks: ['bookmarks'] as const,

  profileRoot: ['profile'] as const,
  profile: (username: string) => ['profile', username] as const,
  profilePosts: (username: string, query: Record<string, string>) =>
    ['profile', username, 'posts', query] as const,
  profileComments: (username: string) => ['profile', username, 'comments'] as const,

  following: ['following'] as const,

  search: (query: string, scope: string) => ['search', query, scope] as const,
  pulse: (params: Record<string, string>) => ['pulse', params] as const,
  trendingTags: (params: Record<string, string>) => ['trending-tags', params] as const,
  map: (window: string) => ['map', window] as const,
  hallOfFame: (params: Record<string, string>) => ['hall-of-fame', params] as const,

  businessRoot: ['business'] as const,
  business: (slug: string) => ['business', slug] as const,
  businessResponses: (slug: string) => ['business', slug, 'responses'] as const,
  businessPosts: (slug: string) => ['business', slug, 'posts'] as const,
  billing: (slug: string) => ['business', slug, 'billing'] as const,
  verificationStatus: (slug: string) => ['business', slug, 'verification'] as const,
  team: (slug: string) => ['business', slug, 'team'] as const,
  invitable: (slug: string, q: string) => ['business', slug, 'invitable', q] as const,
  plans: ['billing', 'plans'] as const,

  mentionSuggestions: (q: string) => ['mention-suggestions', q] as const,

  responseEligibility: (postId: string) => ['response-eligibility', postId] as const,

  watchTerms: (slug: string) => ['watch-terms', slug] as const,
  watchMatches: (slug: string) => ['watch-matches', slug] as const,
  alertPreferences: (slug: string) => ['alert-preferences', slug] as const,

  notifications: ['notifications'] as const,
  unreadCount: ['notifications', 'count'] as const,

  blocked: ['blocked'] as const,
  moderationReports: (status: string) => ['moderation', 'reports', status] as const,
  moderationCount: ['moderation', 'count'] as const,
  moderationAudit: ['moderation', 'audit'] as const,
} as const;
